import { Col, Form, Row, Table, Input, Button } from 'antd';
import { t } from 'i18next';
import { ChangeEventHandler, FC, useCallback, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useUIDispatchContext } from 'providers/UI';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { deleteRole, fetchRolesByParent } from 'services/api/roles';
import { RoleListRow } from 'types/api/roles';

import useOrgEffect from 'hooks/useOrgEffect';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { RolesGroupSelect } from '../components';
import { Private } from 'components/shared';

const Roles: FC = () => {
  const navigate = useNavigate();
  const [roleGroup, setRoleGroup] = useState('');
  const [search, setSearch] = useState('');
  const timer = useRef<NodeJS.Timeout | null>(null);
  const { toggleModal } = useUIDispatchContext();

  const [rows, setRows] = useState<RoleListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    if (!roleGroup) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchRolesByParent(roleGroup);
        if (!cancelled) setRows(res);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [roleGroup, refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const results = rows.filter(item =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        setSearch(e.target.value);
      }, 500);
    },
    []
  );

  const onDeleteRole = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        async onConfirm() {
          try {
            await deleteRole(id);
            refetch();
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [refetch, toggleModal]
  );

  return (
    <PageWrapper
      title="Roles"
      color="white"
      extra={[
        <Private
          key="create-role"
          route={PERMISSION_ROUTES.roles}
          action={PERMISSION_ACTIONS.create}
        >
          <Button
            type="primary"
            onClick={() => navigate('details')}
            disabled={!roleGroup}
          >
            <PlusOutlined />
            Create role
          </Button>
        </Private>
      ]}
    >
      <Row gutter={[16, 0]}>
        <Col sm={6}>
          <Form.Item>
            <Input.Search
              placeholder={t('common.search')}
              onChange={onSearchChange}
            />
          </Form.Item>
        </Col>
        <Col sm={6}>
          <Form.Item>
            <RolesGroupSelect value={roleGroup} onChange={setRoleGroup} />
          </Form.Item>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
          },
          {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value: number) => dayjs(value).format(FULL_DATE_FORMAT)
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'x',
            width: '5%',
            align: 'right',
            render: id => (
              <ActionMenu
                data={[
                  {
                    title: t('table.edit'),
                    permission: {
                      route: PERMISSION_ROUTES.roles,
                      action: PERMISSION_ACTIONS.update
                    },
                    action() {
                      navigate(`details/${id}`);
                    }
                  },
                  {
                    title: t('table.delete'),
                    permission: {
                      route: PERMISSION_ROUTES.roles,
                      action: PERMISSION_ACTIONS.delete
                    },
                    action: () => onDeleteRole(id),
                    menuProps: {
                      style: {
                        color: 'var(--danger-color)'
                      }
                    }
                  }
                ]}
                placement="bottomRight"
                icon={<EllipsisOutlined />}
              />
            )
          }
        ]}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default Roles;
